import React from 'react';
import { Radio, Button } from 'antd';
import PropTypes from 'prop-types';

/**
 * Componente riutilizzabile per i bottoni di tipo toggle
 * Supporta sia Radio.Button di Ant Design che Button standard
 * 
 * @param {Object} props - Proprietà del componente
 * @param {string} props.value - Valore corrente selezionato
 * @param {function} props.onChange - Funzione da chiamare quando cambia la selezione
 * @param {Array} props.options - Array di opzioni [{ value: 'value1', label: 'Label 1', icon: Icon1 }, ...]
 * @param {string} props.type - Tipo di bottoni: 'radio' (Radio.Group) o 'button' (Button standard)
 * @param {Object} props.style - Stile aggiuntivo per il container
 * @param {Object} props.buttonStyle - Stile aggiuntivo per i bottoni
 */
const ToggleButtons = ({
    value,
    onChange,
    options,
    type = 'radio',
    style = {},
    buttonStyle = {}
}) => {
    // Stile di base per il container
    const baseContainerStyle = {
        display: 'inline-block',
        ...style
    };

    // Gestione del cambio valore per i bottoni standard
    const handleButtonClick = (newValue) => {
        onChange(newValue);
    };

    // Gestione del cambio valore per i Radio.Button
    const handleRadioChange = (e) => {
        onChange(e.target.value);
    };

    if (type === 'radio') {
        return (
            <Radio.Group
                value={value}
                onChange={handleRadioChange}
                buttonStyle="solid"
                style={baseContainerStyle}
            >
                {options.map((option, index) => (
                    <Radio.Button
                        key={option.value}
                        value={option.value}
                        style={{
                            borderRadius: index === 0 ? '4px 0 0 4px' : index === options.length - 1 ? '0 4px 4px 0' : '0',
                            padding: '4px 16px',
                            ...buttonStyle
                        }}
                    >
                        {option.icon && <span style={{ marginRight: option.label ? '8px' : 0 }}>{option.icon}</span>}
                        {option.label}
                    </Radio.Button>
                ))}
            </Radio.Group>
        );
    } else {
        return (
            <div style={baseContainerStyle}>
                {options.map((option, index) => (
                    <Button
                        key={option.value}
                        type={value === option.value ? 'primary' : 'default'}
                        onClick={() => handleButtonClick(option.value)}
                        style={{
                            borderRadius: index === 0 ? '4px 0 0 4px' : index === options.length - 1 ? '0 4px 4px 0' : '0',
                            ...buttonStyle
                        }}
                    >
                        {option.icon && <span style={{ marginRight: option.label ? '8px' : 0 }}>{option.icon}</span>}
                        {option.label}
                    </Button>
                ))}
            </div>
        );
    }
};

ToggleButtons.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            icon: PropTypes.node
        })
    ).isRequired,
    type: PropTypes.oneOf(['radio', 'button']),
    style: PropTypes.object,
    buttonStyle: PropTypes.object
};

export default ToggleButtons;
